/**
 * useTasks Hook
 * 
 * Custom hook for managing tasks, badge count, and real-time updates
 */

import { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

export function useTasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [badgeCount, setBadgeCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [streaks, setStreaks] = useState([]);
  const [achievements, setAchievements] = useState([]);

  /**
   * Fetch all tasks
   */
  const fetchTasks = async (filters = {}) => {
    if (!user) return;

    try {
      setLoading(true);
      // Add user_id filter to ensure each user only sees their own tasks
      const userFilters = {
        ...filters,
        user_id: user.id
      };
      const params = new URLSearchParams(userFilters).toString();
      const response = await api.get(`/api/tasks${params ? '?' + params : ''}`);
      
      if (response.data.success) {
        // Handle both response structures for compatibility
        const tasksData = response.data.tasks || response.data.data?.tasks || [];
        // Additional client-side filtering to ensure user isolation
        const userTasks = tasksData.filter(task => task.user_id === user.id);
        setTasks(userTasks);
        updateBadgeCount(userTasks);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Calculate badge count
   */
  const updateBadgeCount = (taskList) => {
    // Note: Settings check removed temporarily - will be added back when settings are properly loaded
    // TODO: Add settings check once settings are loaded properly

    const manualActiveCount = taskList.filter(task => (
      task.status !== 'completed' &&
      task.source !== 'auto_generated'
    )).length;

    setBadgeCount(manualActiveCount);
  };

  /**
   * Create manual task
   */
  const createTask = async (taskData) => {
    try {
      // Ensure the task is assigned to the current user
      const userTaskData = {
        ...taskData,
        user_id: user.id
      };
      const response = await api.post('/api/tasks', userTaskData);
      
      if (response.data.success) {
        await fetchTasks(); // Refresh list
        return { success: true, task: response.data.task };
      }
    } catch (error) {
      console.error('Failed to create task:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Update task
   */
  const updateTask = async (taskId, updates) => {
    try {
      const response = await api.patch(`/api/tasks/${taskId}`, updates);
      
      if (response.data.success) {
        await fetchTasks(); // Refresh list
        return { success: true };
      }
    } catch (error) {
      console.error('Failed to update task:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Delete task
   */
  const deleteTask = async (taskId) => {
    try {
      const response = await api.delete(`/api/tasks/${taskId}`);
      
      if (response.data.success) {
        await fetchTasks(); // Refresh list
        return { success: true };
      }
    } catch (error) {
      console.error('Failed to delete task:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Complete task
   */
  const completeTask = async (taskId) => {
    try {
      const response = await api.post(`/api/tasks/${taskId}/complete`);
      
      if (response.data.success) {
        await fetchTasks(); // Refresh list
        await fetchStats(); // Update stats
        await fetchStreaks(); // Update streaks
        await fetchAchievements(); // Check achievements
        return { success: true };
      }
    } catch (error) {
      console.error('Failed to complete task:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Dismiss task (smart tasks only)
   */
  const dismissTask = async (taskId) => {
    try {
      const response = await api.post(`/api/tasks/${taskId}/dismiss`);
      
      if (response.data.success) {
        await fetchTasks(); // Refresh list
        return { success: true };
      }
    } catch (error) {
      console.error('Failed to dismiss task:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Snooze task
   */
  const snoozeTask = async (taskId, snoozeUntil) => {
    try {
      const response = await api.post(`/api/tasks/${taskId}/snooze`, { snoozeUntil });
      
      if (response.data.success) {
        await fetchTasks(); // Refresh list
        return { success: true };
      }
    } catch (error) {
      console.error('Failed to snooze task:', error);
      return { success: false, error: error.message };
    }
  };

  // Track last fetch time to prevent excessive calls
  const lastFetchTime = useRef(0);
  const FETCH_COOLDOWN = 5000; // 5 seconds cooldown between fetches
  
  /**
   * Fetch statistics
   */
  const fetchStats = async () => {
    const now = Date.now();
    if (now - lastFetchTime.current < FETCH_COOLDOWN) {
      return; // Skip if called too recently
    }
    lastFetchTime.current = now;
    
    try {
      console.log('Fetching stats...');
      const response = await api.get('/api/tasks/stats');
      console.log('Stats response:', response.data);
      if (response.data.success) {
        setStats(response.data.stats);
        console.log('Stats set:', response.data.stats);
      } else {
        console.error('Stats fetch failed:', response.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  /**
   * Fetch streaks
   */
  const fetchStreaks = async () => {
    try {
      const response = await api.get('/api/tasks/streaks');
      if (response.data.success) {
        setStreaks(response.data.streaks || []);
      }
    } catch (error) {
      console.error('Failed to fetch streaks:', error);
    }
  };

  /**
   * Fetch achievements
   */
  const fetchAchievements = async () => {
    try {
      const response = await api.get('/api/tasks/achievements');
      if (response.data.success) {
        setAchievements(response.data.achievements || []);
      }
    } catch (error) {
      console.error('Failed to fetch achievements:', error);
    }
  };

  /**
   * Fetch settings
   */
  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/tasks/settings');
      if (response.data.success) {
        return response.data.settings;
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      return null;
    }
  };

  /**
   * Update settings
   */
  const updateSettings = async (newSettings) => {
    try {
      const response = await api.patch('/tasks/settings', newSettings);
      return { success: response.data.success };
    } catch (error) {
      console.error('Failed to update settings:', error);
      return { success: false, error: error.message };
    }
  };

  // Initial fetch - only when user.id changes, not on every user object change
  const userId = user?.id;
  const lastUserId = useRef(null);
  useEffect(() => {
    if (userId && userId !== lastUserId.current) {
      lastUserId.current = userId;
      fetchTasks();
      fetchStats();
      fetchStreaks();
      fetchAchievements();
    }
  }, [userId]);

  // Poll for updates every 60 seconds - only when userId changes
  useEffect(() => {
    if (!userId) return;

    const interval = setInterval(() => {
      fetchTasks();
      fetchStats();
    }, 60000); // 1 minute

    return () => clearInterval(interval);
  }, [userId]);

  // Listen for task events
  useEffect(() => {
    const handleTaskCreated = () => fetchTasks();
    const handleTaskCompleted = () => {
      fetchTasks();
      fetchStats();
      fetchStreaks();
      fetchAchievements();
    };

    window.addEventListener('taskCreated', handleTaskCreated);
    window.addEventListener('taskCompleted', handleTaskCompleted);

    return () => {
      window.removeEventListener('taskCreated', handleTaskCreated);
      window.removeEventListener('taskCompleted', handleTaskCompleted);
    };
  }, []);

  return {
    tasks,
    loading,
    badgeCount,
    stats,
    streaks,
    achievements,
    fetchTasks,
    createTask,
    updateTask,
    deleteTask,
    completeTask,
    dismissTask,
    snoozeTask,
    fetchSettings,
    updateSettings,
    refreshAll: () => {
      fetchTasks();
      fetchStats();
      fetchStreaks();
      fetchAchievements();
    }
  };
}

